export type ForcaNiveis = { alto: string; medio: string; baixo: string };
export type ForcaPrio = { alto: string[]; medio: string[]; baixo: string[] };

export type ForcaMeta = {
  num: string;
  nome: string;
  icone: string;
  /** A cor da força: barras, bordas, pontos e áreas. */
  hex: string;
  /** A mesma cor, escurecida para texto sobre o fundo claro (≥ 5:1). */
  texto: string;
  /** token CSS ou hex para variável --fc */
  cor: string;
  desc: string;
  temaNames: [string, string];
  niveis: ForcaNiveis;
  prio: ForcaPrio;
};

export const FORCAS_META: readonly ForcaMeta[] = [
  {
    num: '01',
    nome: 'Fundação',
    icone: '🧠',
    hex: '#ffc800',
    texto: '#7a5a00',
    cor: 'var(--c1)',
    desc: 'A base de tudo. Define se você atua como operador ou como estrategista — e se a empresa tem uma direção clara de crescimento.',
    temaNames: ['Mentalidade', 'Estratégia'],
    niveis: {
      alto: 'Sua mentalidade e estratégia estão bem desenvolvidas. Você atua mais como empresário do que como operador. O próximo nível é aprofundar o posicionamento e garantir que a equipe execute com autonomia crescente.',
      medio:
        'Você tem visão estratégica, mas ainda se envolve demais no operacional. Criar sistemas de delegação e revisar a estratégia com regularidade fará grande diferença.',
      baixo:
        'Essa é a fundação mais urgente. Enquanto você estiver preso no operacional, a empresa não crescerá além do seu esforço pessoal. Priorize sair do dia a dia e definir uma direção clara.',
    },
    prio: {
      alto: [
        'Documente e compartilhe a estratégia com a equipe — não apenas com você mesmo',
        'Mapeie as decisões que ainda dependem exclusivamente de você e crie processos para eliminá-las',
        'Reserve ao menos 4 horas semanais para pensar estrategicamente, sem interrupções',
      ],
      medio: [
        'Liste as 5 tarefas que você faz que outra pessoa poderia executar — e delegue ao menos 2 este mês',
        'Escreva em uma página: onde a empresa estará em 2 anos e por que isso importa',
        'Revise o posicionamento: o que você faz de diferente que nenhum concorrente faz igual?',
      ],
      baixo: [
        'Mapeie o que trava quando você se ausenta — e resolva cada ponto de dependência um a um',
        'Escreva uma frase que descreve o rumo da empresa nos próximos 12 meses e compartilhe',
        'Delegue uma tarefa operacional relevante ainda esta semana — e não reassuma',
      ],
    },
  },
  {
    num: '02',
    nome: 'Pessoas',
    icone: '👥',
    hex: '#4a9eff',
    texto: '#1a5fc2',
    cor: 'var(--c2)',
    desc: 'Empresas crescem através de pessoas. Avalia se você tem a cultura certa, líderes emergindo e um time que funciona além da sua presença.',
    temaNames: ['Cultura', 'Liderança'],
    niveis: {
      alto: 'Você tem cultura sólida e líderes se desenvolvendo. O próximo desafio é criar sistemas de desenvolvimento mais estruturados e garantir que a cultura escale com o crescimento.',
      medio:
        'Há um ambiente positivo, mas a cultura ainda depende muito de você. Formalizar valores em comportamentos e criar rituais consistentes fará grande diferença.',
      baixo:
        'Pessoas e cultura são o gargalo atual. Sem um time autônomo, o crescimento fica limitado à sua energia. Comece identificando quem tem potencial de liderança.',
    },
    prio: {
      alto: [
        'Estruture um plano de desenvolvimento individual para os principais talentos',
        'Crie rituais culturais que aconteçam sem depender de você',
        'Mapeie quem pode assumir novas responsabilidades nos próximos 6 meses',
      ],
      medio: [
        'Escreva os valores da empresa com comportamentos concretos: o que fazemos e o que não fazemos',
        'Inicie conversas individuais quinzenais com os membros-chave — 30 min sem pauta de trabalho',
        'Identifique o maior problema de clima que você está tolerando e tome uma ação concreta',
      ],
      baixo: [
        'Identifique quem na equipe tem mais iniciativa e potencial de liderança',
        'Defina 3 valores reais da empresa e torne-os explícitos para todos',
        'Resolva o maior problema de desmotivação ou desalinhamento que existe hoje',
      ],
    },
  },
  {
    num: '03',
    nome: 'Operação',
    icone: '⚙️',
    hex: '#3ecf6a',
    texto: '#146f33',
    cor: 'var(--c3)',
    desc: 'Operação sólida transforma esforço em resultado consistente. Avalia se processos, indicadores e saúde financeira estão sob controle.',
    temaNames: ['Gestão', 'Financeiro'],
    niveis: {
      alto: 'Sua operação está bem estruturada. Processos documentados, indicadores acompanhados e clareza financeira. O próximo nível é otimizar margens e criar sistemas que escalem sem aumentar o esforço.',
      medio:
        'Você tem algum controle, mas a operação ainda depende muito da sua presença. Documentar processos críticos e criar uma rotina financeira mensal será o maior salto.',
      baixo:
        'A operação é o principal risco hoje. Sem processos e clareza financeira, o crescimento gera caos. Comece pelo financeiro: saiba seus números. Depois documente o processo mais crítico.',
    },
    prio: {
      alto: [
        'Identifique e elimine o maior vazamento de margem do negócio',
        'Crie um painel com os 5 indicadores mais importantes, atualizado semanalmente',
        'Teste se seus processos sobrevivem sem você: peça a alguém novo que execute seguindo só a documentação',
      ],
      medio: [
        'Monte o DRE do último mês: receita, custos fixos, variáveis e margem líquida real',
        'Documente o processo crítico que mais depende de você — ainda este mês',
        'Defina dia e hora fixos por mês para fechar os números e tomar decisões financeiras',
      ],
      baixo: [
        'Calcule agora: faturamento bruto, custos fixos totais e margem bruta do último mês',
        'Documente em passo a passo o processo que mais trava quando você não está',
        'Separe as finanças pessoais das finanças da empresa — é o ponto de partida de tudo',
      ],
    },
  },
  {
    num: '04',
    nome: 'Crescimento',
    icone: '📈',
    hex: '#ff6b3d',
    texto: '#b3350c',
    cor: 'var(--c4)',
    desc: 'Crescimento é o motor do negócio. Avalia se a empresa tem processo claro de atração e conversão de clientes — além da presença do dono.',
    temaNames: ['Marketing', 'Vendas'],
    niveis: {
      alto: 'Sua engine de crescimento está funcionando. Você tem clareza de canal, processo de vendas e métricas. O próximo nível é escalar o que funciona e reduzir a dependência do fundador na geração de negócios.',
      medio:
        'Você gera crescimento, mas ainda depende muito da sua presença ou esforço pessoal. Documentar o processo de vendas e entender os números de aquisição será transformador.',
      baixo:
        'Crescimento previsível é urgente. Sem clareza de como atrair e converter clientes de forma consistente, o negócio vive de altos e baixos. Comece definindo o perfil do cliente ideal.',
    },
    prio: {
      alto: [
        'Documente o processo de vendas para que outro possa executar com os mesmos resultados',
        'Calcule e acompanhe CAC e LTV por canal — descubra qual gera mais retorno',
        'Crie um programa estruturado de indicações com os clientes mais satisfeitos',
      ],
      medio: [
        'Entreviste 5 clientes atuais: por que compraram, por que ficaram e o que indicariam a um amigo',
        'Documente o processo de vendas atual, mesmo que imperfeito — comece pelo que você faz hoje',
        'Defina meta de novos clientes por mês e acompanhe semanalmente',
      ],
      baixo: [
        'Escreva em uma página: quem é seu cliente ideal, qual problema tem e por que escolheria você',
        'Liste de onde vieram os últimos 10 clientes — esse é o canal que funciona',
        'Escolha um canal de marketing e execute de forma consistente por 60 dias',
      ],
    },
  },
  {
    num: '05',
    nome: 'Futuro',
    icone: '🚀',
    hex: '#b57bee',
    texto: '#7b3cc4',
    cor: 'var(--c5)',
    desc: 'Empresas que não inovam ficam para trás. Avalia se o negócio usa tecnologia de forma intencional e se existe mentalidade de melhoria contínua.',
    temaNames: ['Tecnologia', 'Inovação'],
    niveis: {
      alto: 'Sua empresa está orientada para o futuro. Tecnologia e inovação já fazem parte da cultura. O próximo passo é aprofundar o uso de IA e criar um processo sistemático de inovação contínua.',
      medio:
        'Você adota tecnologia quando precisa, mas ainda de forma reativa. Criar o hábito de revisar processos com olhar de inovação e explorar IA trará vantagem competitiva relevante.',
      baixo:
        'Tecnologia e inovação ainda estão em segundo plano. Pequenas mudanças já fazem diferença: uma ferramenta nova, um processo automatizado, um experimento por mês.',
    },
    prio: {
      alto: [
        'Mapeie onde a IA pode reduzir custo ou aumentar capacidade sem contratar mais pessoas',
        'Crie um processo formal de inovação: ao menos uma revisão de produto ou serviço por trimestre',
        'Incentive a equipe a sugerir melhorias com critérios claros de avaliação',
      ],
      medio: [
        'Identifique um processo manual que pode ser automatizado — implemente ainda este mês',
        'Experimente uma ferramenta de IA em uma tarefa repetitiva por 30 dias',
        'Defina tempo mensal para revisar o que está desatualizado no negócio',
      ],
      baixo: [
        'Liste todas as ferramentas de tecnologia que sua empresa usa — e as que deveria usar',
        'Escolha um processo manual que te incomoda e simplifique nos próximos 30 dias',
        'Experimente uma ferramenta de IA gratuita em uma tarefa pequena desta semana',
      ],
    },
  },
];
